'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'

export function Sidebar({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) {
  return <nav {...props} className={clsx(className, 'flex h-full min-h-0 flex-col')} />
}

export function SidebarHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-b border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
      )}
    />
  )
}

export function SidebarBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8'
      )}
    />
  )
}

export function SidebarFooter({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
      )}
    />
  )
}

export function SidebarSection({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} data-slot="section" className={clsx(className, 'flex flex-col gap-1')} />
}

export function SidebarDivider({ className, ...props }: React.ComponentPropsWithoutRef<'hr'>) {
  return <hr {...props} className={clsx(className, 'my-4 border-t border-zinc-950/5 lg:-mx-4 dark:border-white/5')} />
}

export function SidebarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-8 flex-1')} />
}

export function SidebarHeading({ className, ...props }: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3 {...props} className={clsx(className, 'mb-1.5 px-2.5 text-[11px]/4 font-medium tracking-[0.04em] text-zinc-500 dark:text-zinc-500')} />
  )
}

// Definizione dei tipi per SidebarItem
type SidebarItemBaseProps = {
  current?: boolean;
  ancestor?: boolean;
  className?: string;
  children: React.ReactNode;
  colorScheme?: 'emerald' | 'red' | 'coral' | 'orange' | 'amber' | 'teal' | 'cyan' | 'blue' | 'indigo' | 'violet' | 'gray' | 'default';
};

type SidebarItemAsButtonProps = SidebarItemBaseProps & {
  href?: never;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'onClick'>;

type SidebarItemAsLinkProps = SidebarItemBaseProps & {
  href: string;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className'>;

type SidebarItemProps = SidebarItemAsButtonProps | SidebarItemAsLinkProps;

export const SidebarItem = forwardRef<HTMLAnchorElement | HTMLButtonElement, SidebarItemProps>(
  function SidebarItem(props, ref) {
    const { current, ancestor, className, children, colorScheme = 'default', ...otherProps } = props;
    
    // Mappa dei colori per hover/active
    const colorClasses = {
      emerald: {
        hover: 'data-hover:bg-emerald-500/10 data-hover:*:data-[slot=icon]:fill-emerald-600',
        active: 'data-active:bg-emerald-500/10 data-active:*:data-[slot=icon]:fill-emerald-600',
        current: 'data-current:bg-emerald-500/10 data-current:text-emerald-800 data-current:*:data-[slot=icon]:fill-emerald-600',
        darkHover: 'dark:data-hover:bg-emerald-500/20 dark:data-hover:*:data-[slot=icon]:fill-emerald-400',
        darkActive: 'dark:data-active:bg-emerald-500/20 dark:data-active:*:data-[slot=icon]:fill-emerald-400',
        darkCurrent: 'dark:data-current:bg-emerald-500/15 dark:data-current:text-emerald-100 dark:data-current:*:data-[slot=icon]:fill-emerald-400',
      },
      red: {
        hover: 'data-hover:bg-red-500/10 data-hover:*:data-[slot=icon]:fill-red-600',
        active: 'data-active:bg-red-500/10 data-active:*:data-[slot=icon]:fill-red-600',
        current: 'data-current:bg-red-500/10 data-current:text-red-800 data-current:*:data-[slot=icon]:fill-red-600',
        darkHover: 'dark:data-hover:bg-red-500/20 dark:data-hover:*:data-[slot=icon]:fill-red-400',
        darkActive: 'dark:data-active:bg-red-500/20 dark:data-active:*:data-[slot=icon]:fill-red-400',
        darkCurrent: 'dark:data-current:bg-red-500/15 dark:data-current:text-red-100 dark:data-current:*:data-[slot=icon]:fill-red-400',
      },
      coral: {
        hover: 'data-hover:bg-[#ef6351]/10 data-hover:*:data-[slot=icon]:fill-[#d94f40]',
        active: 'data-active:bg-[#ef6351]/10 data-active:*:data-[slot=icon]:fill-[#d94f40]',
        current: 'data-current:bg-[#ef6351]/10 data-current:text-[#b9382d] data-current:*:data-[slot=icon]:fill-[#d94f40]',
        darkHover: 'dark:data-hover:bg-[#ef6351]/20 dark:data-hover:*:data-[slot=icon]:fill-[#ff9d91]',
        darkActive: 'dark:data-active:bg-[#ef6351]/20 dark:data-active:*:data-[slot=icon]:fill-[#ff9d91]',
        darkCurrent: 'dark:data-current:bg-[#ef6351]/15 dark:data-current:text-[#ffb0a6] dark:data-current:*:data-[slot=icon]:fill-[#ff9d91]',
      },
      orange: {
        hover: 'data-hover:bg-orange-500/10 data-hover:*:data-[slot=icon]:fill-orange-600',
        active: 'data-active:bg-orange-500/10 data-active:*:data-[slot=icon]:fill-orange-600',
        current: 'data-current:bg-orange-500/10 data-current:text-orange-800 data-current:*:data-[slot=icon]:fill-orange-600',
        darkHover: 'dark:data-hover:bg-orange-500/20 dark:data-hover:*:data-[slot=icon]:fill-orange-400',
        darkActive: 'dark:data-active:bg-orange-500/20 dark:data-active:*:data-[slot=icon]:fill-orange-400',
        darkCurrent: 'dark:data-current:bg-orange-500/15 dark:data-current:text-orange-100 dark:data-current:*:data-[slot=icon]:fill-orange-400',
      },
      teal: {
        hover: 'data-hover:bg-teal-500/10 data-hover:*:data-[slot=icon]:fill-teal-600',
        active: 'data-active:bg-teal-500/10 data-active:*:data-[slot=icon]:fill-teal-600',
        current: 'data-current:bg-teal-500/10 data-current:text-teal-800 data-current:*:data-[slot=icon]:fill-teal-600',
        darkHover: 'dark:data-hover:bg-teal-500/20 dark:data-hover:*:data-[slot=icon]:fill-teal-400',
        darkActive: 'dark:data-active:bg-teal-500/20 dark:data-active:*:data-[slot=icon]:fill-teal-400',
        darkCurrent: 'dark:data-current:bg-teal-500/15 dark:data-current:text-teal-100 dark:data-current:*:data-[slot=icon]:fill-teal-400',
      },
      cyan: {
        hover: 'data-hover:bg-cyan-500/10 data-hover:*:data-[slot=icon]:fill-cyan-600',
        active: 'data-active:bg-cyan-500/10 data-active:*:data-[slot=icon]:fill-cyan-600',
        current: 'data-current:bg-cyan-500/10 data-current:text-cyan-800 data-current:*:data-[slot=icon]:fill-cyan-600',
        darkHover: 'dark:data-hover:bg-cyan-500/20 dark:data-hover:*:data-[slot=icon]:fill-cyan-400',
        darkActive: 'dark:data-active:bg-cyan-500/20 dark:data-active:*:data-[slot=icon]:fill-cyan-400',
        darkCurrent: 'dark:data-current:bg-cyan-500/15 dark:data-current:text-cyan-100 dark:data-current:*:data-[slot=icon]:fill-cyan-400',
      },
      blue: {
        hover: 'data-hover:bg-blue-500/10 data-hover:*:data-[slot=icon]:fill-blue-600',
        active: 'data-active:bg-blue-500/10 data-active:*:data-[slot=icon]:fill-blue-600',
        current: 'data-current:bg-blue-500/10 data-current:text-blue-800 data-current:*:data-[slot=icon]:fill-blue-600',
        darkHover: 'dark:data-hover:bg-blue-500/20 dark:data-hover:*:data-[slot=icon]:fill-blue-400',
        darkActive: 'dark:data-active:bg-blue-500/20 dark:data-active:*:data-[slot=icon]:fill-blue-400',
        darkCurrent: 'dark:data-current:bg-blue-500/15 dark:data-current:text-blue-100 dark:data-current:*:data-[slot=icon]:fill-blue-400',
      },
      indigo: {
        hover: 'data-hover:bg-indigo-500/10 data-hover:*:data-[slot=icon]:fill-indigo-600',
        active: 'data-active:bg-indigo-500/10 data-active:*:data-[slot=icon]:fill-indigo-600',
        current: 'data-current:bg-indigo-500/10 data-current:text-indigo-800 data-current:*:data-[slot=icon]:fill-indigo-600',
        darkHover: 'dark:data-hover:bg-indigo-500/20 dark:data-hover:*:data-[slot=icon]:fill-indigo-400',
        darkActive: 'dark:data-active:bg-indigo-500/20 dark:data-active:*:data-[slot=icon]:fill-indigo-400',
        darkCurrent: 'dark:data-current:bg-indigo-500/15 dark:data-current:text-indigo-100 dark:data-current:*:data-[slot=icon]:fill-indigo-400',
      },
      violet: {
        hover: 'data-hover:bg-violet-500/10 data-hover:*:data-[slot=icon]:fill-violet-600',
        active: 'data-active:bg-violet-500/10 data-active:*:data-[slot=icon]:fill-violet-600',
        current: 'data-current:bg-violet-500/10 data-current:text-violet-800 data-current:*:data-[slot=icon]:fill-violet-600',
        darkHover: 'dark:data-hover:bg-violet-500/20 dark:data-hover:*:data-[slot=icon]:fill-violet-400',
        darkActive: 'dark:data-active:bg-violet-500/20 dark:data-active:*:data-[slot=icon]:fill-violet-400',
        darkCurrent: 'dark:data-current:bg-violet-500/15 dark:data-current:text-violet-100 dark:data-current:*:data-[slot=icon]:fill-violet-400',
      },
      amber: {
        hover: 'data-hover:bg-amber-500/10 data-hover:*:data-[slot=icon]:fill-amber-600',
        active: 'data-active:bg-amber-500/10 data-active:*:data-[slot=icon]:fill-amber-600',
        current: 'data-current:bg-amber-500/10 data-current:text-amber-800 data-current:*:data-[slot=icon]:fill-amber-600',
        darkHover: 'dark:data-hover:bg-amber-500/20 dark:data-hover:*:data-[slot=icon]:fill-amber-400',
        darkActive: 'dark:data-active:bg-amber-500/20 dark:data-active:*:data-[slot=icon]:fill-amber-400',
        darkCurrent: 'dark:data-current:bg-amber-500/15 dark:data-current:text-amber-100 dark:data-current:*:data-[slot=icon]:fill-amber-400',
      },
      gray: {
        hover: 'data-hover:bg-zinc-500/10 data-hover:*:data-[slot=icon]:fill-zinc-600',
        active: 'data-active:bg-zinc-500/10 data-active:*:data-[slot=icon]:fill-zinc-600',
        current: 'data-current:bg-zinc-500/10 data-current:text-zinc-800 data-current:*:data-[slot=icon]:fill-zinc-600',
        darkHover: 'dark:data-hover:bg-zinc-500/20 dark:data-hover:*:data-[slot=icon]:fill-zinc-400',
        darkActive: 'dark:data-active:bg-zinc-500/20 dark:data-active:*:data-[slot=icon]:fill-zinc-400',
        darkCurrent: 'dark:data-current:bg-white/10 dark:data-current:text-white dark:data-current:*:data-[slot=icon]:fill-zinc-300',
      },
      default: {
        hover: 'data-hover:bg-zinc-950/5 data-hover:*:data-[slot=icon]:fill-zinc-950',
        active: 'data-active:bg-zinc-950/5 data-active:*:data-[slot=icon]:fill-zinc-950',
        current: 'data-current:bg-zinc-950/5 data-current:text-zinc-950 data-current:*:data-[slot=icon]:fill-zinc-950',
        darkHover: 'dark:data-hover:bg-white/5 dark:data-hover:*:data-[slot=icon]:fill-white',
        darkActive: 'dark:data-active:bg-white/5 dark:data-active:*:data-[slot=icon]:fill-white',
        darkCurrent: 'dark:data-current:bg-white/10 dark:data-current:text-white dark:data-current:*:data-[slot=icon]:fill-white',
      },
    };

    const ancestorClasses: Record<string, string> = {
      coral: 'data-ancestor:text-[#b9382d] data-ancestor:*:data-[slot=icon]:fill-[#d94f40] dark:data-ancestor:!text-[#ffb0a6] dark:data-ancestor:*:data-[slot=icon]:fill-[#ff9d91]',
      teal: 'data-ancestor:text-teal-700 data-ancestor:*:data-[slot=icon]:fill-teal-600 dark:data-ancestor:!text-teal-100 dark:data-ancestor:*:data-[slot=icon]:fill-teal-400',
      violet: 'data-ancestor:text-violet-700 data-ancestor:*:data-[slot=icon]:fill-violet-600 dark:data-ancestor:!text-violet-100 dark:data-ancestor:*:data-[slot=icon]:fill-violet-400',
      orange: 'data-ancestor:text-orange-700 data-ancestor:*:data-[slot=icon]:fill-orange-600 dark:data-ancestor:!text-orange-100 dark:data-ancestor:*:data-[slot=icon]:fill-orange-400',
      cyan: 'data-ancestor:text-cyan-700 data-ancestor:*:data-[slot=icon]:fill-cyan-600 dark:data-ancestor:!text-cyan-100 dark:data-ancestor:*:data-[slot=icon]:fill-cyan-400',
      amber: 'data-ancestor:text-amber-700 data-ancestor:*:data-[slot=icon]:fill-amber-600 dark:data-ancestor:!text-amber-100 dark:data-ancestor:*:data-[slot=icon]:fill-amber-400',
      blue: 'data-ancestor:text-blue-700 data-ancestor:*:data-[slot=icon]:fill-blue-600 dark:data-ancestor:!text-blue-100 dark:data-ancestor:*:data-[slot=icon]:fill-blue-400',
      gray: 'data-ancestor:text-zinc-700 data-ancestor:*:data-[slot=icon]:fill-zinc-600 dark:data-ancestor:!text-white dark:data-ancestor:*:data-[slot=icon]:fill-zinc-300',
      default: 'data-ancestor:text-zinc-700 data-ancestor:*:data-[slot=icon]:fill-zinc-600 dark:data-ancestor:!text-white dark:data-ancestor:*:data-[slot=icon]:fill-zinc-300',
    };

    const selectedColors = colorClasses[colorScheme];
    
    const classes = clsx(
      // Base
      'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm font-medium text-zinc-950 transition-[background-color,color,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.99]',
      // Leading icon/icon-only
      '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-zinc-500',
      // Trailing icon (down chevron or similar)
      '*:last:data-[slot=icon]:ml-auto *:last:data-[slot=icon]:size-4',
      // Avatar
      '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-6 *:data-[slot=avatar]:[--ring-opacity:10%]',
      // Hover - colori personalizzati
      selectedColors.hover,
      // Active - colori personalizzati
      selectedColors.active,
      // Current - colori personalizzati
      selectedColors.current,
      ancestor && 'bg-zinc-950/[0.035] font-semibold text-zinc-700 dark:bg-white/[0.045] dark:!text-white',
      ancestor && (ancestorClasses[colorScheme] || ancestorClasses.default),
      // Dark mode base
      'dark:text-zinc-300 dark:*:data-[slot=icon]:fill-zinc-400',
      // Dark mode hover/active/current - colori personalizzati
      selectedColors.darkHover,
      selectedColors.darkActive,
      selectedColors.darkCurrent
    );

    return (
      <span className={clsx(className, 'relative')}>
        {'href' in props && props.href !== undefined ? (
          (() => {
            // Rimuovi la prop 'type' se presente
            const { type: _type, ...linkProps } = otherProps as SidebarItemAsLinkProps & { type?: string };
            return (
              <Headless.CloseButton
                as={Link}
                {...linkProps}
                className={classes}
                data-current={current ? 'true' : undefined}
                data-ancestor={ancestor ? 'true' : undefined}
                aria-current={current ? 'page' : undefined}
                ref={ref as React.ForwardedRef<HTMLAnchorElement>}
              >
                <TouchTarget>{children}</TouchTarget>
              </Headless.CloseButton>
            );
          })()
        ) : (
          <Headless.Button
            {...otherProps as SidebarItemAsButtonProps}
            type="button"
            className={clsx('cursor-default', classes)}
            data-current={current ? 'true' : undefined}
            data-ancestor={ancestor ? 'true' : undefined}
            aria-current={current ? 'page' : undefined}
            ref={ref as React.ForwardedRef<HTMLButtonElement>}
          >
            <TouchTarget>{children}</TouchTarget>
          </Headless.Button>
        )}
      </span>
    );
  }
);

export function SidebarLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'truncate')} />
}